// src/components/forms/offersListing.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  apiGetOfferSchedules,
  apiAcceptOffer,
} from "../../api/backend/brandAmbassador";
import { apiFetchCurrentUserDetails } from "../../api/backend/user";
import { ImgTopBtn } from "../../components/buttons";
import { CheckMarkIcon } from "../../components/icons";
import { TIME_ZONES } from "../../constants/time";
import "./offersListing.scss";

// Helper to map zone value to display label
function getZoneLabel(zoneValue) {
  const entry = TIME_ZONES.find((z) => z.value === zoneValue);
  return entry ? entry.label : zoneValue;
}

export default function OffersList() {
  const { userId } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayCount, setDisplayCount] = useState(10);

  // Load user profile (to get their role)
  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetchCurrentUserDetails(userId);
        setProfileData(resp.data);
      } catch (err) {
        console.error(err);
        setError(err);
      }
    })();
  }, [userId]);

  // Load offer schedules
  useEffect(() => {
    if (!profileData) return;
    (async () => {
      try {
        const resp = await apiGetOfferSchedules(userId, profileData.role);
        const raw = Array.isArray(resp.data.schedules) ? resp.data.schedules : [];
        setSchedules(raw);
      } catch (err) {
        console.error("Error fetching schedules:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, profileData]);

  if (loading) return <div>Loading offers...</div>;
  if (error) return <div>Error loading offers.</div>;

  // Prepare, sort, and slice
  const sortedOffers = schedules
    .map((o) => {
      const start = new Date(`${o.date}T${o.startTime}`);
      const end = new Date(`${o.date}T${o.endTime}`);
      const { timeZone } = o;
      return { ...o, start, end, timeZone };
    })
    .sort((a, b) => b.start - a.start);
  const offersToDisplay = sortedOffers.slice(0, displayCount);

  const handleViewMore = () => setDisplayCount((c) => c + 10);
  const handleAcceptOffer = async (offerId) => {
    try {
      await apiAcceptOffer(offerId, {
        sourceId: profileData.id,
        username: profileData.username,
        role: profileData.role,
      });
      setSchedules((curr) => curr.filter((o) => o.id !== offerId));
    } catch (err) {
      console.error(err);
    }
  };

  if (offersToDisplay.length === 0) return <p>No current offers available.</p>;

  return (
    <div id="offerTop" className="offers-list-container">
      <h2>Available Offers</h2>
      <ul className="offers-list">
        {offersToDisplay.map((offer) => {
          const { start, end, location, instructions, id, timeZone } = offer;
          const formattedDate = start.toLocaleDateString(undefined, {
            weekday: "long",
            month: "numeric",
            day: "numeric",
          });
          const formattedStart = start.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          const formattedEnd = end.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          const tzLabel = getZoneLabel(timeZone);

          const acceptBy = new Date(start);
          acceptBy.setHours(acceptBy.getHours() - 2);
          const formattedAcceptBy = acceptBy.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          const durationHours = Math.round((end - start) / 36e5);

          return (
            <li key={id} className="offer-item">
              <div className="offer-details">
                <p>
                  <strong className="label">Date:</strong> {formattedDate}
                </p>
                <p>
                  <strong className="label">Location:</strong> {location}
                </p>
                <p>
                  <strong className="label">Time:</strong> {formattedStart} – {formattedEnd} <em>({tzLabel})</em>
                </p>
                <p>
                  <strong className="label">Duration:</strong> {durationHours} hours
                </p>
                <p>
                  <strong className="label">Instructions:</strong> {instructions}
                </p>
                <p>
                  <strong className="label">Accept by:</strong> {formattedAcceptBy}
                </p>
              </div>
              <div className="offer-action">
                <ImgTopBtn
                  image={<CheckMarkIcon height="15px" width="15px" fillColor="var(--color-bloodred)" />}
                  onClick={() => handleAcceptOffer(id)}
                  imgTopBtnText="Accept Offer"
                  imgTopBtnWidth="100%"
                  imgTopBtnFontColor="var(--color-bloodred)"
                />
              </div>
            </li>
          );
        })}
      </ul>
      {offersToDisplay.length < sortedOffers.length && (
        <button className="view-more-btn" onClick={handleViewMore}>
          View More
        </button>
      )}
    </div>
  );
}
